import { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { useAppData } from "../contexts/AppContext";
import { RouteGate } from "../routes/routeAccess";
import { checkCollaboratorPermission } from "../pages/Users/utils/checkPermission";

interface IRequirePermissionProps {
  /** Permission slug checked against the current client, e.g. "company.read". */
  p: string;
  /**
   * Optional product/access gate, mirroring the Sidebar's menu visibility:
   * the company must have the product (and, for certificates, the user too).
   */
  extra?: RouteGate;
  children: ReactElement;
}

/**
 * Route guard. Deep-linking makes every page reachable by URL, so guarded routes
 * must enforce the same permission AND product gate the Sidebar uses to show the
 * menu item. Redirects to the dashboard ("/") when the user lacks access.
 */
export function RequirePermission({ p, extra, children }: IRequirePermissionProps) {
  const data = useAppData();
  const { validation, currentClient, certificatesLoaded, resourcesLoaded, resourcesError } = data;

  // The data the gate depends on resolves asynchronously after mount: currentClient
  // right after mount, the product flags once the certificate lists load, and the
  // client's resources (jurisdiction gate, GEN-4021) once getResources answers.
  // Treat "not loaded yet" as PENDING (render null) rather than a denial — otherwise
  // a deep-link/F5 onto a product-gated route bounces to "/" before the data arrives.
  // A getResources failure settles the wait: the jurisdiction predicates then see the
  // resources as unknown and deny (fail-closed), while gates that don't use them pass.
  // A espera pelos resources vale só para o gate que os LÊ (`usesResources`): o
  // getResources não tem timeout próprio (cai nos 300 s do ApiCall), e esperar por ele
  // numa rota que não usa jurisdição deixaria a página em branco por minutos.
  const resourcesPending = !!extra?.usesResources && !resourcesLoaded && !resourcesError;
  if (!currentClient || (extra && (!certificatesLoaded || resourcesPending))) {
    return null;
  }

  const allowed = checkCollaboratorPermission(validation, currentClient.guid_client, p) && (extra ? extra(data) : true);

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return children;
}
